import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { adminStats, adminSyncWp } from "../services/api";
import { AdminOrders } from "../components/admin/AdminOrders";
import { AdminDiscounts } from "../components/admin/AdminDiscounts";
import { AdminContacts } from "../components/admin/AdminContacts";
import { AdminAiVisibility } from "../components/admin/AdminAiVisibility";
import { AdminWaitlist } from "../components/admin/AdminWaitlist";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Package, CreditCard, Users, Mail } from "lucide-react";
import Seo from "../components/Seo";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-5 flex items-center gap-4" data-testid={testid}>
    <div className="w-11 h-11 rounded-full bg-wine/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-wine" /></div>
    <div>
      <p className="text-2xl font-serif text-ink leading-none">{value}</p>
      <p className="text-xs text-ink/50 mt-1">{label}</p>
    </div>
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (user === null) navigate("/login?next=/admin");
    if (user && user.role !== "admin") navigate("/account");
    if (user?.role === "admin") adminStats().then(setStats).catch(() => {});
  }, [user, navigate]);

  if (!user || user.role !== "admin") return <div className="pt-40 text-center text-ink/50">…</div>;

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await adminSyncWp();
      toast.success(`Sync abgeschlossen: ${r.products} Produkte, ${r.blog_posts} Blogposts`);
    } catch {
      toast.error("Sync fehlgeschlagen");
    }
    setSyncing(false);
  };

  return (
    <div className="pt-20 min-h-screen bg-cream">
      <Seo title="Admin-Bereich" description="Verwaltung von Bestellungen und Gutscheinen." path="/admin" noindex />
      <div className="max-w-6xl mx-auto px-6 py-14" data-testid="admin-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-wine flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-gold" /></div>
            <div>
              <h1 className="font-serif text-3xl text-ink">Admin-Bereich</h1>
              <p className="text-ink/50 text-sm">Bestellungen, Gutscheine & Anfragen verwalten</p>
            </div>
          </div>
          <Button data-testid="admin-sync-button" variant="outline" onClick={runSync} disabled={syncing} className="gap-2 border-wine/30 text-wine hover:bg-wine hover:text-white">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Synchronisiert…" : "WordPress-Daten neu laden"}
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard icon={Package} label="Bestellungen gesamt" value={stats.orders_total} testid="stat-orders" />
            <StatCard icon={CreditCard} label={`Umsatz (${stats.orders_paid} bezahlt)`} value={`CHF ${stats.revenue.toFixed(2)}`} testid="stat-revenue" />
            <StatCard icon={Users} label="Kunden" value={stats.customers} testid="stat-customers" />
            <StatCard icon={Mail} label="Kontaktanfragen" value={stats.contacts} testid="stat-contacts" />
          </div>
        )}

        <Tabs defaultValue="orders">
          <TabsList className="bg-cream-dark mb-6">
            <TabsTrigger data-testid="tab-orders" value="orders">Bestellungen</TabsTrigger>
            <TabsTrigger data-testid="tab-discounts" value="discounts">Gutscheine</TabsTrigger>
            <TabsTrigger data-testid="tab-contacts" value="contacts">Anfragen</TabsTrigger>
            <TabsTrigger data-testid="tab-ai-visibility" value="ai">KI-Sichtbarkeit</TabsTrigger>
            <TabsTrigger data-testid="tab-waitlist" value="waitlist">Warteliste</TabsTrigger>
          </TabsList>
          <TabsContent value="orders"><AdminOrders /></TabsContent>
          <TabsContent value="discounts"><AdminDiscounts /></TabsContent>
          <TabsContent value="contacts"><AdminContacts /></TabsContent>
          <TabsContent value="ai"><AdminAiVisibility /></TabsContent>
          <TabsContent value="waitlist"><AdminWaitlist /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
